import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { emailAPI, emailProcessingAPI, emailTemplatesAPI, communicationLogAPI, analyticsAPI } from '../../services/api';
import { format, formatDistanceToNow, isPast } from 'date-fns';

const { 
  FiSave, FiMail, FiClock, FiPlus, FiTrash2, FiEdit2, FiSettings, 
  FiCheckCircle, FiAlertCircle, FiSend, FiServer, FiTest, FiPlay, FiPause,
  FiRefreshCw, FiEye, FiEyeOff, FiCopy, FiGlobe, FiLock, FiUser, FiFileText
} = FiIcons;

const EmailSettings = () => {
  const { 
    emailTemplates, setEmailTemplates, 
    emailSequences, setEmailSequences, 
    smtpSettings, setSmtpSettings, 
    cronSettings, setCronSettings 
  } = useData();
  const [templates, setTemplates] = useState(emailTemplates);
  const [activeTab, setActiveTab] = useState('templates');
  const [activeTemplate, setActiveTemplate] = useState('welcome');
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [templateTestEmail, setTemplateTestEmail] = useState('');
  const [isTestingTemplate, setIsTestingTemplate] = useState(false);
  const [pendingEmails, setPendingEmails] = useState([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);
  const [isLoadingSent, setIsLoadingSent] = useState(false);
  const [sentEmailsTotal, setSentEmailsTotal] = useState(0);
  const [sentEmailsPage, setSentEmailsPage] = useState(0);
  const [communications, setCommunications] = useState([]);
  const [isLoadingCommunications, setIsLoadingCommunications] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  // SMTP state
  const [smtpFormData, setSmtpFormData] = useState({
    enabled: false,
    host: '',
    port: '587',
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    secure: false
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Load SMTP settings from API on mount
  useEffect(() => {
    const loadSmtpSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const settings = await emailAPI.getSmtpSettings();
        setSmtpFormData({
          ...settings,
          password: '', // Don't load password from API
        });
      } catch (error) {
        console.error('Error loading SMTP settings:', error);
        // Use defaults if API fails
      } finally {
        setIsLoadingSettings(false);
      }
    };
    loadSmtpSettings();
  }, []);

  // Cron state
  const [cronFormData, setCronFormData] = useState({
    ...cronSettings,
    enabled: cronSettings.enabled || false
  });

  // Load email templates from API
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const apiTemplates = await emailTemplatesAPI.getAll();
        if (apiTemplates && Object.keys(apiTemplates).length > 0) {
          // Merge with defaults to ensure all templates exist
          setTemplates({
            welcome: { subject: '', body: '', sendTime: '10:00', triggerType: null, daysOffset: 0, hoursOffset: 0, enabled: true, ...apiTemplates.welcome },
            checkin_reminder: { subject: '', body: '', sendTime: '10:00', triggerType: null, daysOffset: 0, hoursOffset: 0, enabled: true, ...apiTemplates.checkin_reminder },
            checkout_reminder: { subject: '', body: '', sendTime: '10:00', triggerType: null, daysOffset: 0, hoursOffset: 0, enabled: true, ...apiTemplates.checkout_reminder },
          });
        }
      } catch (error) {
        console.error('Error loading email templates:', error);
        // Use local templates as fallback
      }
    };
    loadTemplates();
  }, []);

  // Load pending emails
  const loadPendingEmails = async () => {
    try {
      setIsLoadingPending(true);
      const result = await emailProcessingAPI.getPending();
      console.log('Pending emails result:', result);
      if (result.success) {
        setPendingEmails(result.pending || []);
        if (result.debug) {
          console.log('Debug info:', result.debug);
        }
      } else {
        console.error('Failed to load pending emails:', result);
        toast.error(result.error || 'Failed to load pending emails');
      }
    } catch (error) {
      console.error('Error loading pending emails:', error);
      toast.error('Failed to load pending emails: ' + error.message);
    } finally {
      setIsLoadingPending(false);
    }
  };

  // Load pending emails when tab is active
  useEffect(() => {
    if (activeTab === 'pending') {
      loadPendingEmails();
      // Refresh every 30 seconds when on pending tab
      const interval = setInterval(loadPendingEmails, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Load sent emails
  const loadSentEmails = async (page = 0) => {
    try {
      setIsLoadingSent(true);
      const result = await emailProcessingAPI.getHistory(null, 50, page * 50);
      if (result.success) {
        setSentEmails(result.emails || []);
        setSentEmailsTotal(result.total || 0);
        setSentEmailsPage(page);
      }
    } catch (error) {
      console.error('Error loading sent emails:', error);
      toast.error('Failed to load sent emails');
    } finally {
      setIsLoadingSent(false);
    }
  };

  // Load sent emails when tab is active
  useEffect(() => {
    if (activeTab === 'sent') {
      loadSentEmails(0);
    }
  }, [activeTab]);

  // Load communications when tab is active
  const loadCommunications = async () => {
    try {
      setIsLoadingCommunications(true);
      const result = await communicationLogAPI.getLog();
      if (result.success) {
        setCommunications(result.communications || []);
      }
    } catch (error) {
      console.error('Error loading communications:', error);
      toast.error('Failed to load communication log');
    } finally {
      setIsLoadingCommunications(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'communications') {
      loadCommunications();
      const interval = setInterval(loadCommunications, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Load analytics when tab is active
  const loadAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      const result = await analyticsAPI.getEmailStats(30);
      if (result.success) {
        setAnalytics(result);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  const handleSaveChanges = async () => {
    try {
      setIsSavingSettings(true);
      
      // Save templates to API
      for (const [templateType, templateData] of Object.entries(templates)) {
        await emailTemplatesAPI.update(templateType, templateData);
      }
      
      // Save to local state
      setEmailTemplates(templates);
      setCronSettings(cronFormData);
      
      // Save SMTP settings to API
      await emailAPI.updateSmtpSettings(smtpFormData);
      setSmtpSettings(smtpFormData);
      
      toast.success('All email settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save email settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleTemplateChange = (e) => {
    const value = e.target.type === 'number' 
      ? parseInt(e.target.value, 10) || 0
      : e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    
    setTemplates({
      ...templates,
      [activeTemplate]: {
        ...templates[activeTemplate],
        [e.target.name]: value,
      },
    });
  };

  const handleSmtpChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSmtpFormData({
      ...smtpFormData,
      [e.target.name]: value
    });
  };

  const handleCronChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCronFormData({
      ...cronFormData,
      [e.target.name]: value
    });
  };

  const testSmtpConnection = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    if (!smtpFormData.host || !smtpFormData.username || !smtpFormData.password) {
      toast.error('Please configure all SMTP settings first');
      return;
    }

    setIsTestingEmail(true);
    try {
      // First test the SMTP connection
      const connectionTest = await emailAPI.testSmtpConnection(smtpFormData);
      
      if (!connectionTest.success) {
        toast.error(connectionTest.error || 'SMTP connection failed');
        return;
      }
      
      // If connection successful, send a test email
      const emailResult = await emailAPI.sendEmail({
        to: testEmail,
        subject: 'Test Email from Guest Portal',
        html: `
          <h2>Test Email</h2>
          <p>This is a test email from your Guest Portal Platform.</p>
          <p>If you received this, your SMTP configuration is working correctly!</p>
          <hr>
          <p style="color: #666; font-size: 12px;">Sent at ${new Date().toLocaleString()}</p>
        `,
        testEmail: testEmail
      });
      
      if (emailResult.success) {
        toast.success(`Test email sent successfully to ${testEmail}!`);
        setTestEmail('');
      } else {
        toast.error(emailResult.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast.error(error.message || 'Failed to send test email. Please check your SMTP configuration.');
    } finally {
      setIsTestingEmail(false);
    }
  };

  const testTemplateEmail = async () => {
    if (!templateTestEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    if (!templates[activeTemplate]?.subject || !templates[activeTemplate]?.body) {
      toast.error('Please fill in the subject and body for this template first');
      return;
    }

    setIsTestingTemplate(true);
    try {
      // First save the template to ensure it's in the database
      await emailTemplatesAPI.update(activeTemplate, templates[activeTemplate]);
      
      // Then send the test email
      const result = await emailTemplatesAPI.test(activeTemplate, templateTestEmail);
      
      if (result.success) {
        toast.success(`Test email sent successfully to ${templateTestEmail}!`);
        setTemplateTestEmail('');
      } else {
        toast.error(result.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test template email error:', error);
      toast.error(error.message || 'Failed to send test email. Please check your SMTP configuration.');
    } finally {
      setIsTestingTemplate(false);
    }
  };


  const getCronExpression = () => {
    const { frequency, customCron } = cronFormData;
    if (frequency === 'hourly') return '0 * * * *';
    if (frequency === 'daily') return '0 9 * * *';
    if (frequency === 'weekly') return '0 9 * * 1';
    if (frequency === 'monthly') return '0 9 1 * *';
    return customCron || '0 * * * *';
  };

  const getNextRunTime = () => {
    const now = new Date();
    const { frequency } = cronFormData;
    
    if (frequency === 'hourly') {
      const nextRun = new Date(now.getTime() + 60 * 60 * 1000);
      return nextRun.toLocaleString();
    }
    if (frequency === 'daily') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow.toLocaleString();
    }
    if (frequency === 'weekly') {
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(9, 0, 0, 0);
      return nextWeek.toLocaleString();
    }
    return 'Not scheduled';
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Email Configuration</h2>
          <p className="text-sm text-gray-500">Manage templates and delivery settings</p>
        </div>
        <motion.button 
          onClick={handleSaveChanges}
          disabled={isSavingSettings || isLoadingSettings}
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }} 
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl shadow-lg shadow-blue-200 transition-all duration-200 font-medium"
        >
          <SafeIcon icon={FiSave} />
          <span>{isSavingSettings ? 'Saving...' : 'Save Settings'}</span>
        </motion.button>
      </div>

      {/* Modern Tab Navigation */}
      <div className="bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 inline-flex mb-8">
        {[
          { key: 'templates', label: 'Templates', icon: FiMail },
          { key: 'pending', label: 'Pending Emails', icon: FiClock },
          { key: 'sent', label: 'Sent Emails', icon: FiCheckCircle },
          { key: 'communications', label: 'Communication Log', icon: FiUser },
          { key: 'analytics', label: 'Analytics', icon: FiSettings },
          { key: 'smtp', label: 'SMTP Server', icon: FiServer },
          { key: 'cron', label: 'Schedule', icon: FiClock },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-3xl shadow-sm border border-transparent p-8 min-h-[500px]">
        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 border-r border-gray-100 pr-8">
              <div className="mb-4">
                <h3 className="font-bold text-gray-900">Select Template</h3>
              </div>
              <div className="space-y-2">
                {[
                  { key: 'welcome', label: 'Welcome Email' },
                  { key: 'checkin_reminder', label: 'Check-in Reminder' },
                  { key: 'checkout_reminder', label: 'Check-out Reminder' },
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTemplate(t.key)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      activeTemplate === t.key 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Subject Line</label>
                  <motion.button
                    type="button"
                    onClick={testTemplateEmail}
                    disabled={isTestingTemplate || !smtpFormData.enabled}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    title="Send a test email with this template"
                  >
                    <SafeIcon icon={FiSend} className="text-sm" />
                    <span>{isTestingTemplate ? 'Sending...' : 'Test Email'}</span>
                  </motion.button>
                </div>
                <input 
                  type="text" 
                  name="subject" 
                  value={templates[activeTemplate].subject} 
                  onChange={handleTemplateChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                />
                {!smtpFormData.enabled && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ Enable SMTP in the SMTP Server tab to send test emails
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Body</label>
                <textarea 
                  name="body" 
                  value={templates[activeTemplate].body} 
                  onChange={handleTemplateChange}
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Available variables: {'{{propertyName}}'}, {'{{guestName}}'}, {'{{portalLink}}'}, {'{{checkInDate}}'}, {'{{checkOutDate}}'}, {'{{checkOutTime}}'}
                </p>
              </div>
              
              {/* Test Email Input */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Test Email Address</label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={templateTestEmail}
                    onChange={(e) => setTemplateTestEmail(e.target.value)}
                    placeholder="your-email@example.com"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <motion.button
                    type="button"
                    onClick={testTemplateEmail}
                    disabled={isTestingTemplate || !templateTestEmail || !smtpFormData.enabled}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    {isTestingTemplate ? 'Sending...' : 'Send Test'}
                  </motion.button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Send a test email with sample data to preview how this template will look
                </p>
              </div>
              
              {/* Schedule Settings for Template */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Schedule Settings</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={templates[activeTemplate]?.enabled !== false}
                      onChange={handleTemplateChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {templates[activeTemplate]?.enabled !== false ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Trigger Type</label>
                    <select
                      name="triggerType"
                      value={templates[activeTemplate]?.triggerType || ''}
                      onChange={handleTemplateChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    >
                      <option value="">Not scheduled</option>
                      <option value="checkin">Check-in Time</option>
                      <option value="checkout">Check-out Time</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select when to send relative to guest check-in or check-out
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Send Time</label>
                    <input
                      type="time"
                      name="sendTime"
                      value={templates[activeTemplate]?.sendTime || '10:00'}
                      onChange={handleTemplateChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Time of day to send the email
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Days Offset</label>
                    <input
                      type="number"
                      name="daysOffset"
                      value={templates[activeTemplate]?.daysOffset || 0}
                      onChange={handleTemplateChange}
                      min="-30"
                      max="30"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {templates[activeTemplate]?.daysOffset > 0 
                        ? `${templates[activeTemplate].daysOffset} days after` 
                        : templates[activeTemplate]?.daysOffset < 0 
                        ? `${Math.abs(templates[activeTemplate].daysOffset)} days before`
                        : 'On the day'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Hours Offset</label>
                    <input
                      type="number"
                      name="hoursOffset"
                      value={templates[activeTemplate]?.hoursOffset || 0}
                      onChange={handleTemplateChange}
                      min="-24"
                      max="24"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {templates[activeTemplate]?.hoursOffset > 0 
                        ? `${templates[activeTemplate].hoursOffset} hours after` 
                        : templates[activeTemplate]?.hoursOffset < 0 
                        ? `${Math.abs(templates[activeTemplate].hoursOffset)} hours before`
                        : 'At the send time'}
                    </p>
                  </div>
                </div>
                {templates[activeTemplate]?.triggerType && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-sm text-blue-800">
                      <strong>Schedule Summary:</strong> This email will be sent{' '}
                      {templates[activeTemplate].daysOffset !== 0 && (
                        <>
                          {Math.abs(templates[activeTemplate].daysOffset)} day(s){' '}
                          {templates[activeTemplate].daysOffset > 0 ? 'after' : 'before'}{' '}
                        </>
                      )}
                      {templates[activeTemplate].hoursOffset !== 0 && (
                        <>
                          {Math.abs(templates[activeTemplate].hoursOffset)} hour(s){' '}
                          {templates[activeTemplate].hoursOffset > 0 ? 'after' : 'before'}{' '}
                        </>
                      )}
                      {templates[activeTemplate].daysOffset === 0 && templates[activeTemplate].hoursOffset === 0 && 'at '}
                      the guest's <strong>{templates[activeTemplate].triggerType === 'checkin' ? 'check-in' : 'check-out'}</strong> time, 
                      at <strong>{templates[activeTemplate].sendTime || '10:00'}</strong>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SMTP Settings Tab */}
        {activeTab === 'smtp' && (
          <div className="max-w-3xl mx-auto space-y-8">
            {isLoadingSettings && (
              <div className="text-center py-4 text-gray-500">
                Loading SMTP settings...
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiAlertCircle} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">SMTP Configuration</h4>
                  <p className="text-sm text-blue-800">
                    Configure your SMTP server to send automated emails to guests. 
                    Use Gmail, Outlook, or your preferred email service.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">SMTP Host</label>
                <input
                  type="text"
                  name="host"
                  value={smtpFormData.host}
                  onChange={handleSmtpChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Port</label>
                <select
                  name="port"
                  value={smtpFormData.port}
                  onChange={(e) => {
                    const port = e.target.value;
                    const portNum = parseInt(port);
                    setSmtpFormData({
                      ...smtpFormData,
                      port,
                      // Port 465 = SSL (secure: true), Port 587 = STARTTLS (secure: false)
                      secure: portNum === 465
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                >
                  <option value="587">587 (TLS)</option>
                  <option value="465">465 (SSL)</option>
                  <option value="25">25 (Unencrypted)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                <input
                  type="email"
                  name="username"
                  value={smtpFormData.username}
                  onChange={handleSmtpChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="your-email@gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showSmtpPassword ? 'text' : 'password'}
                    name="password"
                    value={smtpFormData.password}
                    onChange={handleSmtpChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    placeholder="Your app password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={showSmtpPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">From Email</label>
                <input
                  type="email"
                  name="fromEmail"
                  value={smtpFormData.fromEmail}
                  onChange={handleSmtpChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="noreply@yourproperty.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">From Name</label>
                <input
                  type="text"
                  name="fromName"
                  value={smtpFormData.fromName}
                  onChange={handleSmtpChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="Your Property Name"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <h4 className="font-semibold text-gray-900">Use Secure Connection (SSL/TLS)</h4>
                <p className="text-sm text-gray-600">Enable for encrypted connections (auto-enabled for port 465)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="secure"
                  checked={smtpFormData.secure}
                  onChange={handleSmtpChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <h4 className="font-semibold text-gray-900">Enable SMTP</h4>
                <p className="text-sm text-gray-600">Send emails using configured SMTP server</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={smtpFormData.enabled}
                  onChange={handleSmtpChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Test Email Section */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="font-semibold text-gray-900 mb-4">Test Configuration</h4>
              <div className="flex space-x-4">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  placeholder="Enter test email address"
                />
                <motion.button
                  type="button"
                  onClick={testSmtpConnection}
                  disabled={isTestingEmail || !smtpFormData.enabled}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl transition-colors duration-200"
                >
                  <SafeIcon icon={FiSend} />
                  <span>{isTestingEmail ? 'Sending...' : 'Send Test'}</span>
                </motion.button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Emails Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Pending Emails</h3>
                <p className="text-sm text-gray-500 mt-1">
                  View all scheduled emails that will be sent automatically
                </p>
              </div>
              <button
                onClick={loadPendingEmails}
                disabled={isLoadingPending}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiRefreshCw} className={isLoadingPending ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {isLoadingPending ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-4">Loading pending emails...</p>
              </div>
            ) : pendingEmails.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <SafeIcon icon={FiMail} className="mx-auto text-gray-400 text-4xl mb-4" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">No Pending Emails</h4>
                <p className="text-sm text-gray-500">
                  There are no scheduled emails. Emails will appear here once you configure templates with scheduling.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingEmails.map((email) => {
                  const scheduledDate = new Date(email.scheduledTime);
                  const isPastDue = isPast(scheduledDate);
                  
                  return (
                    <div
                      key={email.id}
                      className={`border rounded-xl p-6 transition-all ${
                        isPastDue
                          ? 'border-orange-200 bg-orange-50'
                          : 'border-gray-200 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              email.templateType === 'welcome'
                                ? 'bg-blue-100 text-blue-700'
                                : email.templateType === 'checkin_reminder'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {email.templateName}
                            </div>
                            {isPastDue && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                                Past Due
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Guest</p>
                              <p className="font-semibold text-gray-900">{email.guestName}</p>
                              <p className="text-sm text-gray-600">{email.guestEmail}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Property</p>
                              <p className="font-semibold text-gray-900">{email.propertyName}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Scheduled Time</p>
                              <p className="font-medium text-gray-900">
                                {format(scheduledDate, 'MMM d, yyyy h:mm a')}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {isPastDue ? 'Was ' : ''}{formatDistanceToNow(scheduledDate, { addSuffix: true })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Trigger</p>
                              <p className="font-medium text-gray-900 capitalize">
                                {email.triggerType === 'checkin' ? 'Check-in' : 'Check-out'}
                              </p>
                              {email.checkInDate && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Check-in: {format(new Date(email.checkInDate), 'MMM d, yyyy')}
                                </p>
                              )}
                              {email.checkOutDate && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Check-out: {format(new Date(email.checkOutDate), 'MMM d, yyyy')}
                                </p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Days Until</p>
                              <p className={`font-semibold ${
                                email.daysUntil <= 0
                                  ? 'text-orange-600'
                                  : email.daysUntil <= 1
                                  ? 'text-yellow-600'
                                  : 'text-gray-900'
                              }`}>
                                {email.daysUntil <= 0
                                  ? 'Due now'
                                  : email.daysUntil === 1
                                  ? 'Tomorrow'
                                  : `${email.daysUntil} days`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Communication Log Tab */}
        {activeTab === 'communications' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Communication Log</h3>
                <p className="text-sm text-gray-500 mt-1">
                  View all communications with guests in one place
                </p>
              </div>
              <button
                onClick={loadCommunications}
                disabled={isLoadingCommunications}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiRefreshCw} className={isLoadingCommunications ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {isLoadingCommunications ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-4">Loading communications...</p>
              </div>
            ) : communications.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <SafeIcon icon={FiUser} className="mx-auto text-gray-400 text-4xl mb-4" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">No Communications</h4>
                <p className="text-sm text-gray-500">
                  All emails, messages, and notes will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {communications.map((comm) => {
                  const commDate = new Date(comm.created_at);
                  const isEmail = comm.communication_type === 'email';
                  const isNote = comm.communication_type === 'note';
                  
                  return (
                    <div
                      key={comm.id}
                      className="border rounded-xl p-6 bg-white hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${
                            isEmail ? 'bg-blue-100 text-blue-700' :
                            isNote ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            <SafeIcon icon={isEmail ? FiMail : isNote ? FiFileText : FiUser} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {comm.guest_name || 'Guest'}
                            </p>
                            <p className="text-sm text-gray-500">{comm.property_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {format(commDate, 'MMM d, yyyy')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(commDate, 'h:mm a')}
                          </p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            comm.direction === 'outbound' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {comm.direction === 'outbound' ? 'Outbound' : 'Inbound'}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                            {comm.communication_type}
                          </span>
                          {comm.status && (
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              comm.status === 'sent' ? 'bg-green-100 text-green-700' :
                              comm.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {comm.status}
                            </span>
                          )}
                        </div>
                        {comm.subject && (
                          <p className="font-medium text-gray-900 mb-1">{comm.subject}</p>
                        )}
                        {comm.content && (
                          <p className="text-sm text-gray-600 line-clamp-2">{comm.content}</p>
                        )}
                      </div>

                      {comm.metadata && typeof comm.metadata === 'object' && (
                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                          {comm.metadata.template_type && (
                            <span className="mr-3">Template: {comm.metadata.template_type}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Email Analytics</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Track email performance and engagement metrics
                </p>
              </div>
              <button
                onClick={loadAnalytics}
                disabled={isLoadingAnalytics}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiRefreshCw} className={isLoadingAnalytics ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
            </div>

            {isLoadingAnalytics ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-4">Loading analytics...</p>
              </div>
            ) : !analytics ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <SafeIcon icon={FiSettings} className="mx-auto text-gray-400 text-4xl mb-4" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">No Data Yet</h4>
                <p className="text-sm text-gray-500">
                  Analytics will appear here once you start sending emails.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-blue-700">Total Sent</p>
                      <SafeIcon icon={FiMail} className="text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-blue-900">{analytics.stats?.total_sent || 0}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-green-700">Opened</p>
                      <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                    </div>
                    <p className="text-3xl font-bold text-green-900">{analytics.stats?.total_opened || 0}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-purple-700">Open Rate</p>
                      <SafeIcon icon={FiEye} className="text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-purple-900">{analytics.stats?.openRate || 0}%</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Guests Contacted</p>
                      <SafeIcon icon={FiUser} className="text-gray-600" />
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{analytics.stats?.guests_contacted || 0}</p>
                  </div>
                </div>

                {/* Template Performance */}
                {analytics.templateStats && analytics.templateStats.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Template Performance</h4>
                    <div className="space-y-4">
                      {analytics.templateStats.map((stat) => {
                        const openRate = stat.sent_count > 0 
                          ? ((parseInt(stat.opened_count) / parseInt(stat.sent_count)) * 100).toFixed(1)
                          : 0;
                        return (
                          <div key={stat.template_type} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900 capitalize">
                                {stat.template_type.replace('_', ' ')}
                              </p>
                              <span className="text-sm text-gray-500">{openRate}% open rate</span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm">
                              <span className="text-gray-600">Sent: {stat.sent_count}</span>
                              <span className="text-green-600">Opened: {stat.opened_count}</span>
                            </div>
                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full transition-all"
                                style={{ width: `${openRate}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Failed Emails */}
                {analytics.stats?.total_failed > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiAlertCircle} className="text-red-600" />
                      <h4 className="text-lg font-semibold text-red-900">Failed Emails</h4>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{analytics.stats.total_failed}</p>
                    <p className="text-sm text-red-600 mt-1">Check your SMTP settings if this number is high</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cron Settings Tab */}
        {activeTab === 'cron' && (
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiClock} className="text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">Scheduled Email Processing</h4>
                  <p className="text-sm text-green-800">
                    Configure automated email processing schedules. The system will check 
                    for pending emails and send them according to your schedule.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Frequency</label>
                <select
                  name="frequency"
                  value={cronFormData.frequency}
                  onChange={handleCronChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily (9:00 AM)</option>
                  <option value="weekly">Weekly (Monday 9:00 AM)</option>
                  <option value="monthly">Monthly (1st 9:00 AM)</option>
                  <option value="custom">Custom Cron Expression</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Timezone</label>
                <select
                  name="timezone"
                  value={cronFormData.timezone}
                  onChange={handleCronChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>

              {cronFormData.frequency === 'custom' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Custom Cron Expression</label>
                  <input
                    type="text"
                    name="customCron"
                    value={cronFormData.customCron}
                    onChange={handleCronChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm bg-gray-50"
                    placeholder="0 * * * *"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: minute hour day month weekday (e.g., "0 */6 * * *" for every 6 hours)
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Retry Attempts</label>
                <input
                  type="number"
                  name="retryAttempts"
                  value={cronFormData.retryAttempts}
                  onChange={handleCronChange}
                  min="0"
                  max="10"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Retry Delay (minutes)</label>
                <input
                  type="number"
                  name="retryDelay"
                  value={cronFormData.retryDelay}
                  onChange={handleCronChange}
                  min="1"
                  max="60"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <h4 className="font-semibold text-gray-900">Enable Scheduled Processing</h4>
                <p className="text-sm text-gray-600">Automatically process and send emails on schedule</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={cronFormData.enabled}
                  onChange={handleCronChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Schedule Status */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Schedule Status</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Cron Expression</p>
                  <div className="flex items-center space-x-2">
                    <code className="bg-gray-200 px-3 py-1 rounded text-sm font-mono">
                      {getCronExpression()}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(getCronExpression())}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiCopy} />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Next Run</p>
                  <p className="text-sm font-medium text-gray-900">
                    {cronFormData.enabled ? getNextRunTime() : 'Disabled'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Last Run</p>
                  <p className="text-sm font-medium text-gray-900">
                    {cronFormData.lastRun ? new Date(cronFormData.lastRun).toLocaleString() : 'Never run'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${cronFormData.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm font-medium text-gray-900">
                      {cronFormData.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Manual Trigger / Test Cronjob */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="font-semibold text-gray-900 mb-4">Test Email Processing</h4>
              <p className="text-sm text-gray-500 mb-4">
                Manually trigger email processing to test your cronjob. This will check all active guest pages 
                and send emails based on their check-in/checkout dates and your email sequences.
              </p>
              <motion.button
                type="button"
                onClick={async () => {
                  try {
                    setIsTestingEmail(true);
                    const result = await emailProcessingAPI.processScheduled();
                    if (result.success) {
                      toast.success(`Successfully processed ${result.processed} email(s)!`);
                      if (result.emails && result.emails.length > 0) {
                        console.log('Sent emails:', result.emails);
                      }
                      if (result.errors && result.errors.length > 0) {
                        toast.error(`Some errors occurred: ${result.errors.join(', ')}`);
                      }
                    } else {
                      toast.error(result.error || 'Failed to process emails');
                    }
                  } catch (error) {
                    console.error('Error processing emails:', error);
                    toast.error(error.message || 'Failed to process scheduled emails');
                  } finally {
                    setIsTestingEmail(false);
                  }
                }}
                disabled={isTestingEmail || !cronFormData.enabled}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl transition-colors duration-200"
              >
                <SafeIcon icon={FiRefreshCw} className={isTestingEmail ? 'animate-spin' : ''} />
                <span>{isTestingEmail ? 'Processing...' : 'Test Email Processing Now'}</span>
              </motion.button>
              {!cronFormData.enabled && (
                <p className="text-xs text-yellow-600 mt-2">
                  ⚠️ Enable scheduled processing above to test email sending
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;